import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  User, 
  Tag, 
  ArrowLeft, 
  Share2, 
  Bookmark, 
  MessageSquare, 
  Eye, 
  Heart,
  Facebook,
  Twitter,
  Linkedin,
  Copy,
  Clock,
  TrendingUp,
  RefreshCw,
  Home,
  Search,
  AlertCircle
} from 'lucide-react';
import enhancedBlogService, { EnhancedBlog } from '../services/enhancedBlogService';
import CommentSection from '../components/CommentSection';
import NewsletterSubscription from '../components/NewsletterSubscription';
import { fixImageURL, fixAvatarURL } from '../utils/imageUtils';
import CRMONCELogo from '../components/CRMONCELogo';

// Using the EnhancedBlog interface from the service
type BlogPost = EnhancedBlog;

interface Comment {
  id: string;
  author: string;
  avatar: string;
  content: string;
  createdAt: string;
  likes: number;
}

const BlogDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [blogPost, setBlogPost] = useState<BlogPost | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Debug logging
  console.log('🔍 BlogDetail component mounted with slug:', slug);
  console.log('🔍 BlogDetail current state:', { isLoading, error: !!error, blogPost: !!blogPost });
  const [isLiked, setIsLiked] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [showComments, setShowComments] = useState(false);

  // Fetch blog post from API with better error handling
  useEffect(() => {
    const fetchBlogPost = async () => {
      if (!slug) {
        setError('No blog slug provided');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        
        console.log('🔍 BlogDetail: Starting to fetch blog with slug:', slug);
        console.log('🔍 BlogDetail: Using enhancedBlogService.getBlogBySlug');
        
        const response = await enhancedBlogService.getBlogBySlug(slug);
        
        console.log('🔍 BlogDetail: API response received:', response);
        
        if (response) {
          console.log('✅ BlogDetail: Blog loaded successfully:', response.title);
          setBlogPost(response);
        } else {
          console.error('❌ BlogDetail: No blog data received');
          setError('Blog not found');
        }
      } catch (error: any) {
        console.error('❌ BlogDetail: Error fetching blog post:', error);
        console.error('❌ BlogDetail: Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        setError(error.response?.data?.message || error.message || 'Failed to load blog post');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBlogPost();
  }, [slug]);

  // Function to force refresh the blog post (bypass cache)
  const handleRefresh = async () => {
    if (!slug) return;
    
    setIsLoading(true);
    setError(null);
    try {
      const response = await enhancedBlogService.refreshBlog(slug);
      setBlogPost(response);
    } catch (error: any) {
      console.error('Error refreshing blog post:', error);
      setError(error.response?.data?.message || error.message || 'Failed to refresh blog post');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLike = () => {
    setIsLiked(!isLiked);
    if (blogPost) {
      setBlogPost({
        ...blogPost,
        likes: isLiked ? blogPost.likes - 1 : blogPost.likes + 1
      });
    }
  };

  const handleBookmark = () => {
    setIsBookmarked(!isBookmarked);
  };

  const handleShare = (platform: string) => {
    const url = window.location.href;
    const title = blogPost?.title || '';
    
    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`);
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`);
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`);
        break;
      case 'copy':
        navigator.clipboard.writeText(url);
        // You could add a toast notification here
        break;
    }
    setShowShareMenu(false);
  };

  const handleCommentSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newComment.trim()) {
      const comment: Comment = {
        id: Date.now().toString(),
        author: 'Current User',
        avatar: '/api/placeholder/32/32',
        content: newComment,
        createdAt: new Date().toISOString(),
        likes: 0
      };
      setComments([comment, ...comments]);
      setNewComment('');
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'Unknown date';
    }
  };

  // Loading state with better design
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="container-custom">
            <div className="flex items-center justify-between h-16">
              <button
                onClick={() => navigate('/blog')}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                <ArrowLeft size={20} />
                <span>Back to Blog</span>
              </button>
              
              <div className="flex items-center space-x-4">
                <CRMONCELogo className="h-8 w-auto" />
              </div>
            </div>
          </div>
        </header>

        <div className="container-custom py-12">
          <div className="max-w-4xl mx-auto">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-6"></div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">Loading Blog Post</h2>
              <p className="text-gray-600">Please wait while we fetch the content...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state with better design
  if (error || (!isLoading && !blogPost)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <SEOHead 
          title="Blog Post Not Found - CRMONCE"
          description="The blog post you're looking for could not be found. Browse our other Microsoft Dynamics 365 and Power Platform insights."
          keywords="blog not found, CRMONCE blog, Dynamics 365 insights"
          canonical="/blog"
          noIndex={true}
        />
        
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="container-custom">
            <div className="flex items-center justify-between h-16">
              <button
                onClick={() => navigate('/blog')}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                <ArrowLeft size={20} />
                <span>Back to Blog</span>
              </button>
              
              <div className="flex items-center space-x-4">
                <CRMONCELogo className="h-8 w-auto" />
              </div>
            </div>
          </div>
        </header>

        <div className="container-custom py-12">
          <div className="max-w-2xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200"
            >
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                {error ? 'Blog Post Not Found' : 'Something went wrong'}
              </h1>
              
              <p className="text-gray-600 mb-8 text-lg">
                {error || 'The blog post you\'re looking for doesn\'t exist or has been removed.'}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={() => {
                    console.log('🔄 Retrying blog fetch for slug:', slug);
                    window.location.reload();
                  }}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Retry Loading
                </button>
                <button 
                  onClick={() => navigate('/blog')}
                  className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors font-medium"
                >
                  Back to Blog
                </button>
              </div>
              
              {/* Debug Information for Development */}
              {process.env.NODE_ENV === 'development' && (
                <div className="mt-8 p-4 bg-gray-100 rounded-lg text-left">
                  <h3 className="font-semibold mb-2">Debug Information:</h3>
                  <div className="text-sm space-y-1">
                    <p><strong>Slug:</strong> {slug || 'No slug provided'}</p>
                    <p><strong>Error:</strong> {error || 'No error'}</p>
                    <p><strong>Loading:</strong> {isLoading.toString()}</p>
                    <p><strong>Blog Post:</strong> {blogPost ? 'Found' : 'Not found'}</p>
                    <p><strong>API URL:</strong> {`/api/enhanced-blog/${slug}`}</p>
                    <p><strong>Current URL:</strong> {window.location.href}</p>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  // Add null check for TypeScript safety
  if (!blogPost) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <SEOHead 
          title="Loading Blog Post - CRMONCE"
          description="Loading Microsoft Dynamics 365 and Power Platform insights from CRMONCE experts."
          keywords="CRMONCE blog, Dynamics 365, Power Platform"
          canonical="/blog"
        />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
            <p className="text-gray-600">Loading blog post...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <SEOHead 
        title={blogPost.title}
        description={blogPost.excerpt || `Read ${blogPost.title} - Expert insights on Microsoft Dynamics 365 and Power Platform from CRMONCE.`}
        keywords={`${blogPost.title}, ${blogPost.tags?.join(', ')}, Dynamics 365, Power Platform, CRMONCE blog`}
        canonical={`/blog/${blogPost.slug}`}
        ogImage={blogPost.featuredImage || '/images/og-image.jpg'}
        ogType="article"
      />
      
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="container-custom">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate('/blog')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft size={20} />
              <span>Back to Blog</span>
            </button>
            
            <div className="flex items-center space-x-4">
              <button
                onClick={handleRefresh}
                disabled={isLoading}
                className="p-2 rounded-lg text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors disabled:opacity-50"
                title="Refresh content"
              >
                <RefreshCw size={20} className={isLoading ? 'animate-spin' : ''} />
              </button>
              
              <button
                onClick={handleBookmark}
                className={`p-2 rounded-lg transition-colors ${
                  isBookmarked 
                    ? 'text-yellow-500 bg-yellow-50' 
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                }`}
              >
                <Bookmark size={20} fill={isBookmarked ? 'currentColor' : 'none'} />
              </button>
              
              <div className="relative">
                <button
                  onClick={() => setShowShareMenu(!showShareMenu)}
                  className="p-2 rounded-lg text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors"
                >
                  <Share2 size={20} />
                </button>
                
                {showShareMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute top-full right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 z-50"
                  >
                    <div className="p-3 space-y-2">
                      <button
                        onClick={() => handleShare('facebook')}
                        className="w-full flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
                      >
                        <Facebook size={16} className="text-blue-600" />
                        <span>Facebook</span>
                      </button>
                      <button
                        onClick={() => handleShare('twitter')}
                        className="w-full flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
                      >
                        <Twitter size={16} className="text-blue-400" />
                        <span>Twitter</span>
                      </button>
                      <button
                        onClick={() => handleShare('linkedin')}
                        className="w-full flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
                      >
                        <Linkedin size={16} className="text-blue-700" />
                        <span>LinkedIn</span>
                      </button>
                      <button
                        onClick={() => handleShare('copy')}
                        className="w-full flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
                      >
                        <Copy size={16} className="text-gray-600" />
                        <span>Copy Link</span>
                      </button>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="container-custom py-8">
        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
          {/* Blog Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8 text-left"
          >
            {/* Category Badge */}
            <div className="inline-block bg-blue-100 text-blue-800 text-sm font-medium px-4 py-2 rounded-full mb-6">
              {blogPost.category || 'General'}
            </div>
            
            {/* Title */}
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight text-left">
              {blogPost.title}
            </h1>
            
            {/* Excerpt */}
            {blogPost.excerpt && (
              <p className="text-xl text-gray-600 mb-6 leading-relaxed text-left max-w-4xl">
                {blogPost.excerpt}
              </p>
            )}
            
            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 mb-6 text-left">
              <div className="flex items-center space-x-2">
                <User size={16} />
                <span>{blogPost.authorName || 'CRMONCE Team'}</span>
              </div>
              {blogPost.publishedAt && (
                <div className="flex items-center space-x-2">
                  <Calendar size={16} />
                  <span>{formatDate(blogPost.publishedAt)}</span>
                </div>
              )}
              <div className="flex items-center space-x-2">
                <Clock size={16} />
                <span>{blogPost.readTime || 5} min read</span>
              </div>
              <div className="flex items-center space-x-2">
                <Eye size={16} />
                <span>{(blogPost.views || 0).toLocaleString()} views</span>
              </div>
            </div>
            
            {/* Tags */}
            {blogPost.tags && blogPost.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 text-left">
                {blogPost.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="bg-gray-100 text-gray-700 text-sm px-3 py-1 rounded-full hover:bg-gray-200 transition-colors cursor-pointer"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </motion.div>

          {/* Featured Image */}
          {blogPost.featuredImage && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-8"
            >
              <img
                src={fixImageURL(blogPost.featuredImage)}
                alt={blogPost.title}
                className="w-full h-64 md:h-96 object-cover rounded-2xl shadow-lg"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </motion.div>
          )}

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-white rounded-2xl p-8 md:p-12 shadow-sm border border-gray-200 mb-8"
          >
            <div 
              className="prose prose-lg max-w-none text-left
                prose-headings:text-gray-900 prose-headings:font-bold prose-headings:mb-6 prose-headings:mt-10 prose-headings:text-left
                prose-h1:text-3xl prose-h1:font-bold prose-h1:mb-8 prose-h1:mt-0 prose-h1:text-left
                prose-h2:text-2xl prose-h2:font-semibold prose-h2:mb-6 prose-h2:mt-10 prose-h2:text-left
                prose-h3:text-xl prose-h3:font-semibold prose-h3:mb-4 prose-h3:mt-8 prose-h3:text-left
                prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6 prose-p:text-base prose-p:text-left prose-p:text-justify prose-p:mt-0 prose-p:first:mt-0 prose-p:last:mb-0
                prose-li:text-gray-700 prose-li:leading-relaxed prose-li:mb-2 prose-li:text-left
                prose-ul:mb-8 prose-ul:pl-6 prose-ul:text-left prose-ul:mt-4
                prose-ol:mb-8 prose-ol:pl-6 prose-ol:text-left prose-ol:mt-4
                prose-strong:text-gray-900 prose-strong:font-semibold
                prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline
                prose-blockquote:border-l-4 prose-blockquote:border-blue-500 prose-blockquote:pl-6 prose-blockquote:italic prose-blockquote:text-gray-600 prose-blockquote:text-left prose-blockquote:mb-8 prose-blockquote:mt-4
                prose-code:bg-gray-100 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-sm
                prose-pre:bg-gray-900 prose-pre:text-white prose-pre:p-4 prose-pre:rounded-lg prose-pre:overflow-x-auto prose-pre:mb-8 prose-pre:mt-4
                prose-table:text-left prose-th:text-left prose-td:text-left
                [&>p]:mb-6 [&>p]:mt-0 [&>p:first-child]:mt-0 [&>p:last-child]:mb-0
                [&>h2]:mb-6 [&>h2]:mt-10 [&>h2:first-child]:mt-0
                [&>ul]:mb-8 [&>ul]:mt-4 [&>ul:first-child]:mt-0
                [&>ol]:mb-8 [&>ol]:mt-4 [&>ol:first-child]:mt-0"
              dangerouslySetInnerHTML={{ __html: blogPost.content }}
            />
          </motion.div>

          {/* Author Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 mb-8"
          >
            <div className="flex items-start space-x-4">
              <img
                src={fixAvatarURL(blogPost.authorAvatar)}
                alt={blogPost.authorName || 'CRMONCE Team'}
                className="w-16 h-16 rounded-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face';
                }}
              />
              <div className="flex-1 text-left">
                <h3 className="text-xl font-semibold text-gray-900 mb-2 text-left">
                  About {blogPost.authorName || 'CRMONCE Team'}
                </h3>
                <p className="text-gray-600 leading-relaxed text-left text-justify">
                  {blogPost.authorBio || 'Expert in Dynamics 365 and Power Platform solutions with years of experience in enterprise implementations.'}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Engagement Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 mb-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900 text-left">Engage with this post</h3>
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleLike}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                    isLiked 
                      ? 'text-red-500 bg-red-50' 
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                  }`}
                >
                  <Heart size={20} fill={isLiked ? 'currentColor' : 'none'} />
                  <span>{blogPost.likes || 0}</span>
                </button>
                
                <button
                  onClick={() => setShowComments(!showComments)}
                  className="flex items-center space-x-2 px-4 py-2 rounded-lg text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors"
                >
                  <MessageSquare size={20} />
                  <span>{comments.length}</span>
                </button>
              </div>
            </div>
            
            {/* Comment Form */}
            <form onSubmit={handleCommentSubmit} className="mb-6">
              <div className="flex space-x-3">
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Comment
                </button>
              </div>
            </form>
            
            {/* Comments Section */}
            {showComments && (
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900 text-left">Comments ({comments.length})</h4>
                {comments.map((comment) => (
                  <div key={comment.id} className="flex space-x-3 p-4 bg-gray-50 rounded-lg">
                    <img
                      src={comment.avatar}
                      alt={comment.author}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-medium text-gray-900">{comment.author}</span>
                        <span className="text-sm text-gray-500">
                          {formatDate(comment.createdAt)}
                        </span>
                      </div>
                      <p className="text-gray-700 text-left">{comment.content}</p>
                      <div className="flex items-center space-x-4 mt-2">
                        <button className="text-sm text-gray-500 hover:text-gray-700 transition-colors">
                          Like ({comment.likes})
                        </button>
                        <button className="text-sm text-gray-500 hover:text-gray-700 transition-colors">
                          Reply
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Newsletter Subscription */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.9 }}
            className="mb-8"
          >
            <NewsletterSubscription 
              variant="default"
              source="blog"
              title="Stay Updated"
              description="Get the latest blog posts, case studies, and industry insights delivered directly to your inbox."
              showNameFields={false}
            />
          </motion.div>

          {/* Comments Section */}
          {blogPost && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1.0 }}
            >
              <CommentSection 
                contentType="blog" 
                contentId={parseInt(blogPost.id)} 
                title={blogPost.title}
              />
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogDetail;
